import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getToken() && !document.cookie.includes("access_token")) {
        setLoading(false);
        return;
      }
      try {
        const me = await authApi.me();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  // Phone OTP verification is skipped — register now hydrates the session
  // directly using the { user, token } returned by the backend.
  const register = useCallback(async (input) => {
    const res = await authApi.register(input);
    setUser(res.user);
    return res.user;
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const u = await authApi.login(email, password);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  const updateProfile = useCallback(async () => refreshUser(), [refreshUser]);

  const value = useMemo(
    () => ({
      user, loading,
      isAuthenticated: !!user,
      isAdmin: user?.role === "admin",
      register, login, logout, updateProfile, refreshUser, setUser,
    }),
    [user, loading, register, login, logout, updateProfile, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
